import { Accordion } from '@/components/ui/accordion';
import { useLocation } from 'react-router-dom';
import { useCallback, useMemo } from 'react';
import { AccountNavArchived } from './account-nav-archived';
import { AccountNavGroups } from './account-nav-groups';
import { getAccountGroups, useAccounts, useArchivedAccounts, useBudgetId } from '@liquidbudget/common';
import { useUiStore } from '@/components/ui/use-ui-store';

type Props = {};

/**
 * Creates a safe accordion value from a group name by converting to lowercase
 * and replacing spaces with hyphens.
 */
function createAccordionValue(groupName: string): string {
  return groupName.toLowerCase().replace(/\s+/g, '-');
}

export function AccountNav({}: Props) {
  const location = useLocation();
  const accounts = useAccounts();
  const inactiveAccounts = useArchivedAccounts();
  const budgetId = useBudgetId();

  const { groups } = useMemo(() => getAccountGroups(accounts), [accounts]);
  const budgetState = useUiStore((state) => state.budgets[budgetId]);
  const setAccountNavGroupCollapsed = useUiStore((state) => state.setAccountNavGroupCollapsed);

  // Determine which accordion sections should be open
  const accordionValues = useMemo(() => {
    const values: string[] = [];

    // Add open groups (default to open if no stored preference)
    groups.forEach((group) => {
      const accordionValue = createAccordionValue(group.name);
      const isCollapsed = budgetState?.accountNavGroupsCollapsed?.[group.name] ?? false;

      if (!isCollapsed) {
        values.push(accordionValue);
      }
    });

    // Handle archived accounts - add if not collapsed (default to closed)
    const isArchivedCollapsed = budgetState?.accountNavGroupsCollapsed?.['Archived'] ?? true;
    if (!isArchivedCollapsed) {
      values.push('archived');
    }

    return values;
  }, [groups, budgetState?.accountNavGroupsCollapsed, location.pathname, inactiveAccounts]);

  // Handle accordion state changes
  const handleAccordionChange = useCallback(
    (openValues: string[]) => {
      // Handle account groups
      groups.forEach((group) => {
        const accordionValue = createAccordionValue(group.name);
        const isOpen = openValues.includes(accordionValue);
        setAccountNavGroupCollapsed(budgetId, group.name, !isOpen);
      });

      // Handle archived section
      const isArchivedOpen = openValues.includes('archived');
      setAccountNavGroupCollapsed(budgetId, 'Archived', !isArchivedOpen);
    },
    [groups, budgetId, setAccountNavGroupCollapsed],
  );

  return (
    <Accordion type="multiple" className="w-full" value={accordionValues} onValueChange={handleAccordionChange}>
      <AccountNavGroups />
      <AccountNavArchived />
    </Accordion>
  );
}
